<script setup lang="ts">
/**
 * SyncProgressPopover - Voortgang van een lopende Figma-sync
 *
 * Globale popover (linksonder, App.vue) die tijdens een sync laat zien wat
 * er gebeurt: welk bestand, welke fase, hoeveel lagen er al verwerkt zijn,
 * en waarom het even kan duren. De data komt live binnen via de
 * sync-events op het gebruikerskanaal (zie stores/figma.ts); de
 * klaar-melding loopt via de gewone toast zodat die ook aankomt als de
 * popover is weggeklikt.
 */

import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useFigmaStore } from '../../stores/figma'
import { useLocaleStore } from '../../stores/locale'
import { RefreshCw, X } from 'lucide-vue-next'

const { t } = useI18n()
const figmaStore = useFigmaStore()
const localeStore = useLocaleStore()

// Weggeklikt voor déze sync; een volgende sync (ander bestand of nieuwe
// run) laat de popover gewoon weer zien.
const dismissedForFileId = ref<number | null>(null)

const zichtbaar = computed(() => {
  const status = figmaStore.syncStatus
  if (!status || status.status !== 'syncing') return false
  return dismissedForFileId.value !== status.fileId
})

// Alleen voortgang die bij de húidige sync hoort: een laat binnengekomen
// tussenstand van een vorige sync (ander bestand) mag hier niet doorheen
// schemeren.
const progress = computed(() => {
  const p = figmaStore.syncProgress
  const status = figmaStore.syncStatus
  if (!p || !status || p.fileId !== status.fileId) return null
  return p
})

const naam = computed(
  () => progress.value?.fileName || figmaStore.syncStatus?.fileName || ''
)

const faseTekst = computed(() => {
  const p = progress.value
  if (!p || p.fase === 'ophalen') return t('figma.syncPopover.fetching')
  if (p.fase === 'afronden') return t('figma.syncPopover.finishing')
  if (p.totaal) {
    return t('figma.syncPopover.processing', {
      done: formatAantal(p.verwerkt ?? 0),
      total: formatAantal(p.totaal),
    })
  }
  return t('figma.syncPopover.processingShort')
})

// Alleen de verwerk-fase heeft een echte teller; de andere fases tonen een
// lopend (indeterminate) balkje.
const percentage = computed(() => {
  const p = progress.value
  if (!p || p.fase !== 'verwerken' || !p.totaal) return null
  return Math.min(100, Math.round(((p.verwerkt ?? 0) / p.totaal) * 100))
})

function formatAantal(n: number): string {
  return n.toLocaleString(localeStore.locale)
}

function dismiss() {
  dismissedForFileId.value = figmaStore.syncStatus?.fileId ?? null
}
</script>

<template>
  <Transition
    enter-active-class="transition duration-200 ease-out"
    enter-from-class="translate-y-2 opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transition duration-150 ease-in"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="translate-y-2 opacity-0"
  >
    <div
      v-if="zichtbaar"
      class="fixed bottom-4 left-4 z-40 w-80 max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded-xl shadow-lg p-4"
      role="status"
      aria-live="polite"
    >
      <div class="flex items-start gap-3">
        <div class="flex-shrink-0 w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center">
          <RefreshCw class="w-5 h-5 text-primary-600 animate-spin" aria-hidden="true" />
        </div>

        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-gray-900">
            {{ t('figma.syncPopover.title') }}
          </p>
          <p v-if="naam" class="text-xs text-gray-500 truncate" :title="naam">
            {{ naam }}
          </p>
        </div>

        <!-- gray-500, niet 400: bedienbare iconen vragen 3:1 (WCAG 1.4.11) -->
        <button
          @click="dismiss"
          class="flex-shrink-0 p-1 -m-1 text-gray-500 hover:text-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
          :aria-label="t('figma.syncPopover.dismiss')"
        >
          <X class="w-4 h-4" aria-hidden="true" />
        </button>
      </div>

      <p class="mt-3 text-xs font-medium text-gray-700">
        {{ faseTekst }}
      </p>

      <!-- Voortgangsbalk: percentage tijdens het verwerken, lopend balkje
           tijdens ophalen/afronden -->
      <div class="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden" aria-hidden="true">
        <div
          v-if="percentage !== null"
          class="h-full bg-primary-500 rounded-full transition-[width] duration-500 ease-out"
          :style="{ width: `${percentage}%` }"
        ></div>
        <div v-else class="h-full w-2/5 bg-primary-500 rounded-full balk-lopend"></div>
      </div>

      <p class="mt-3 text-xs text-gray-500 leading-relaxed">
        {{ t('figma.syncPopover.explainer') }}
      </p>
    </div>
  </Transition>
</template>

<style scoped>
@keyframes balk-lopend {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(350%);
  }
}

.balk-lopend {
  animation: balk-lopend 1.4s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .balk-lopend {
    animation: none;
    transform: none;
    width: 100%;
    opacity: 0.4;
  }
}
</style>
